import React, { useState } from 'react';
import { Layout, ProtectedRoute } from '../components/layout';
import { Card, CardBody, Button, LoadingSpinner, Modal } from '../components/common';
import { usePlugins, useEnablePlugin, useDisablePlugin, useUpdatePluginConfig } from '../hooks/usePlugins';

const PluginCard = ({ plugin, onConfigure }) => {
  const enablePlugin = useEnablePlugin();
  const disablePlugin = useDisablePlugin();

  const handleToggle = async () => {
    if (plugin.enabled) {
      await disablePlugin.mutateAsync(plugin.name);
    } else {
      await enablePlugin.mutateAsync(plugin.name);
    }
  };

  const isToggling = enablePlugin.isPending || disablePlugin.isPending;

  return (
    <Card className="mb-4">
      <CardBody>
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <h3 className="text-lg font-semibold">{plugin.name}</h3>
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                v{plugin.version}
              </span>
              <span
                className={`text-xs px-2 py-1 rounded ${
                  plugin.enabled
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-500'
                }`}
              >
                {plugin.enabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <p className="text-gray-600 text-sm">{plugin.description}</p>
          </div>

          <div className="flex gap-2 flex-shrink-0">
            {plugin.enabled && Object.keys(plugin.configSchema || {}).length > 0 && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => onConfigure(plugin)}
              >
                Configure
              </Button>
            )}
            <Button
              variant={plugin.enabled ? 'danger' : 'primary'}
              size="sm"
              onClick={handleToggle}
              disabled={isToggling}
            >
              {isToggling ? '...' : plugin.enabled ? 'Disable' : 'Enable'}
            </Button>
          </div>
        </div>

        {plugin.enabled && plugin.configSchema && Object.keys(plugin.configSchema).length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Configuration</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              {Object.entries(plugin.configSchema).map(([key, schema]) => (
                <div key={key} className="flex items-center gap-2">
                  <span className="text-gray-500">{schema.label || key}:</span>
                  <span className="text-gray-700">
                    {schema.secret ? '••••••••' : schema.default || '-'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

const ConfigureModal = ({ plugin, isOpen, onClose }) => {
  const [config, setConfig] = useState({});
  const updateConfig = useUpdatePluginConfig();

  if (!plugin) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    await updateConfig.mutateAsync({ name: plugin.name, config });
    onClose();
  };

  const handleChange = (key, value) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
  };

  const renderField = (key, schema) => {
    switch (schema.type) {
      case 'select':
        return (
          <select
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={config[key] ?? schema.default ?? ''}
            onChange={(e) => handleChange(key, e.target.value)}
          >
            {schema.options?.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        );
      case 'number':
        return (
          <input
            type="number"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={config[key] ?? schema.default ?? ''}
            min={schema.min}
            max={schema.max}
            onChange={(e) => handleChange(key, parseInt(e.target.value, 10))}
          />
        );
      default:
        return (
          <input
            type={schema.secret ? 'password' : 'text'}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={config[key] ?? ''}
            placeholder={schema.secret ? '••••••••' : schema.default || ''}
            onChange={(e) => handleChange(key, e.target.value)}
          />
        );
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Configure ${plugin.name}`}>
      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          {Object.entries(plugin.configSchema || {}).map(([key, schema]) => (
            <div key={key}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {schema.label || key}
                {schema.required && <span className="text-red-500 ml-1">*</span>}
              </label>
              {renderField(key, schema)}
              {schema.description && (
                <p className="text-xs text-gray-500 mt-1">{schema.description}</p>
              )}
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={updateConfig.isPending}>
            {updateConfig.isPending ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export const Admin = () => {
  const { data, isLoading, error } = usePlugins();
  const [configurePlugin, setConfigurePlugin] = useState(null);

  return (
    <ProtectedRoute>
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-2xl font-bold">Plugin Management</h1>
              <p className="text-gray-600 mt-1">
                Manage installed plugins and their configuration
              </p>
            </div>
          </div>

          {isLoading && (
            <div className="flex justify-center py-12">
              <LoadingSpinner />
            </div>
          )}

          {error && (
            <Card className="mb-4">
              <CardBody>
                <p className="text-red-600">Failed to load plugins: {error.message}</p>
              </CardBody>
            </Card>
          )}

          {data?.plugins?.length === 0 && (
            <Card>
              <CardBody>
                <p className="text-gray-500 text-center py-8">
                  No plugins installed. Plugins can be added to the backend/plugins directory.
                </p>
              </CardBody>
            </Card>
          )}

          {data?.plugins?.map((plugin) => (
            <PluginCard
              key={plugin.name}
              plugin={plugin}
              onConfigure={setConfigurePlugin}
            />
          ))}

          <ConfigureModal
            plugin={configurePlugin}
            isOpen={!!configurePlugin}
            onClose={() => setConfigurePlugin(null)}
          />
        </div>
      </Layout>
    </ProtectedRoute>
  );
};
